import { Dot, Menu } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import store from 'store';
import { switchUser } from '@/actions';
import { getA2PComplianceStatus } from '@/actions/a2pCompliance';
import { isSignupSendingBlocked } from '@/components/signup/activation';
import { getShowNewLayout } from '@/components/sidebar/layout-preference';
import {
  handleDisableNotificationsOnLogout,
  listenForPushMessages,
  setUpNotifications,
} from '@/actions/browserNotifications';

import { getBusinessGroup } from '@/actions/businessGroup';
import { getDepartments } from '@/actions/departments';
import { getAllFolders } from '@/actions/folders';
import { getTemplateFolders } from '@/actions/templateFolders';
import { getMessageAutomations } from '@/actions/messageAutomation';
import { fetchAllReminders } from '@/actions/reminders';
import { getAllTags } from '@/actions/tags';
import { getInitialTeamConversations } from '@/actions/teams';
import { getTemplates } from '@/actions/templates';
import { getCustomerConversations, setCustomerConversationAssignedType } from '@/actions/webchat';
import AnnouncementButton from '@/components/announcements/AnnouncementButton';
import ConnectionChecker from '@/components/dashboards/navigation/ConnectionChecker';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { fetchPendingEscalations } from '@/slices/aiEmployeeSlice';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';
import ReactivateAccountForm from '../../settings/forms/billing/ReactivateAccountForm';
import { RESELLER_DEACTIVATED_MESSAGE } from '@/components/sidebar/ReactivateAccountButton';
import { AccountSwitcher } from './AccountSwitcher';
import { ModeToggle } from './ModeToggle';
import { NavDropdownMenu } from './NavDropdownMenu';
import PhoneVerificationChecker from './PhoneVerificationChecker';

// Helper functions for reminder calculations
const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getEndOfToday = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return today;
};

const enterpriseMessengerComponents = [
  {
    title: 'Inbox',
    description: 'Your conversations with customers across all channels.',
    name: 'inbox',
    type: 'ALL',
  },
  {
    title: 'Group Chat',
    description: 'Create team chats for projects, events, and more.',
    name: 'group-chat',
    type: 'GROUPS',
  },
];

const runnerComponents = [
  {
    title: 'Home',
    description: 'Get an overview of your business',
    name: 'runner',
  },
];

const messengerComponents = [
  {
    title: 'Inbox',
    description: 'Your conversations with customers across all channels.',
    name: 'inbox',
    type: 'ALL',
  },
  {
    title: 'Group Chat',
    description: 'Create team chats for projects, events, and more.',
    name: 'groupchat/groups',
    type: 'GROUPS',
  },
  {
    title: 'Campaigns & Scheduled Messages',
    description: 'Send bulk messages to multiple contacts.',
    name: 'campaigns',
  },

  {
    title: 'Reminders',
    description: 'Create reminders for tasks, follow ups, and more.',
    name: 'reminders',
  },
  {
    title: 'Templates',
    description: 'Create message templates for quick replies.',
    name: 'templates',
  },

  {
    title: 'Call History',
    description: 'View and manage your call records.',
    name: 'call-history',
    type: 'VOICE',
  },
  {
    title: 'Contacts',
    description: 'View and manage all your customer contact information.',
    name: 'contacts',
  },
  {
    title: 'Analytics',
    description: 'See your message stats.',
    name: 'messenger-analytics',
  },
];

const reviewComponents = [
  {
    title: 'Overview',
    description: 'Get a pulse on your online reputation.',
    name: 'reviews',
  },
  {
    title: 'Leaderboard',
    description: 'Who gets reviews on your team.',
    name: 'reviews/leaderboard',
  },
  {
    title: 'Competitors',
    description: 'How do you compare to local competitors.',
    name: 'reviews/competitor',
  },
];

const automationComponents = [
  {
    title: 'Events / Triggers',
    description: 'Connect to your CRM or other tools using webhooks.',
    name: 'automations/message-automations',
  },
  {
    title: 'Smart Replies',
    description: 'Automatically respond to common questions.',
    name: 'automations/smart-replies',
  },
  {
    title: 'Zapier',
    description: 'Connect to thousands of other apps using Zapier.',
    name: 'automations/zapier',
  },
];

const payComponents = [
  {
    title: 'Overview',
    description: 'A simple system to invoice and collect payments via text.',
    name: 'payments',
  },
];

const questionnaireComponents = [
  {
    title: 'Create Forms',
    description: 'Create forms to collect information.',
    name: 'forms',
  },
];

export default function NavBar() {
  const dispatch = useDispatch();

  const unreadConversations = 0;

  const auth = useSelector((state) => state.auth);
  const pendingSignup = isSignupSendingBlocked(auth);
  const showNewLayout = getShowNewLayout(auth);
  const businessGroup = useSelector((state) => state.businessGroup);
  const reminders = useSelector((state) => state.reminders);

  const isEnterpriseEnabled = auth.user.isEnterpriseEnabled;
  const hasVoiceProduct = auth.products?.includes('voice');

  const filteredMessengerComponents = React.useMemo(
    () => messengerComponents.filter((c) => c.type !== 'VOICE' || hasVoiceProduct),
    [hasVoiceProduct],
  );

  const [showReactivateAccountForm, setShowReactivateAccountForm] = React.useState(false);

  const [menuTitle, setMenuTitle] = React.useState('Messenger');

  const location = useLocation();

  // activeDashboard is location.pathname with initial slash removed (e.g., /inbox -> inbox)
  // also need to remove anything passed the word after the first slash
  const activeDashboard = location.pathname.split('/')[1] || '';

  const teams = useSelector((state) => state.teams);

  const teamConversations = teams.teamConversations || [];

  const unreadTeamConversations = teamConversations
    ? teamConversations.filter((convo) => convo._unreadUsers.includes(auth.user._id))
    : [];

  const handleSetCustomerConversationAssignedType = (type) => {
    dispatch(setCustomerConversationAssignedType(type));
  };

  const unreadTeamConversationsLength = unreadTeamConversations.length;

  // Calculate reminder counts
  const allReminders = reminders.reminders || [];

  const dueTodayCount = React.useMemo(() => {
    if (!allReminders || !Array.isArray(allReminders)) {
      return 0;
    }
    const todayStart = getStartOfToday();
    const todayEnd = getEndOfToday();
    return allReminders.filter((reminder) => {
      const reminderDate = new Date(reminder.dateTime);
      return !reminder.isCompleted && reminderDate >= todayStart && reminderDate <= todayEnd;
    }).length;
  }, [allReminders]);

  const overdueCount = React.useMemo(() => {
    if (!allReminders || !Array.isArray(allReminders)) {
      return 0;
    }
    const todayStart = getStartOfToday();
    return allReminders.filter((reminder) => {
      const reminderDate = new Date(reminder.dateTime);
      return !reminder.isCompleted && reminderDate < todayStart;
    }).length;
  }, [allReminders]);

  // menu selection functions for selecting activeBusiness
  // selected business is business object
  // goToRootUrl determines whether to go to the root url or not
  const handleSwitchLocation = (selectedBusiness, goToRootUrl = true) => {
    dispatch(switchUser({ _business: selectedBusiness._id, goToRootUrl }));
  };

  // run initial setup
  React.useEffect(() => {
    const setup = async () => {
      dispatch(getBusinessGroup());
      dispatch(getCustomerConversations({ initial: true }));
      if (hasAiEmployeeProduct(auth)) {
        dispatch(fetchPendingEscalations());
      }
      dispatch(getTemplates(auth.user.isEnterpriseEnabled));
      dispatch(getAllTags(auth.user.isEnterpriseEnabled));
      dispatch(getAllFolders());
      dispatch(getTemplateFolders());
      dispatch(
        getDepartments({
          isEnterpriseRequested: auth.user.isEnterpriseEnabled,
        }),
      );
      dispatch(getMessageAutomations());
      if (!pendingSignup) dispatch(getA2PComplianceStatus());
      dispatch(getInitialTeamConversations());
      dispatch(
        fetchAllReminders({
          dateRange: {
            from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
            to: new Date(new Date().setMonth(new Date().getMonth() + 1)),
          },
        }),
      );
      if (!pendingSignup) {
        listenForPushMessages();
        dispatch(setUpNotifications());
      }
    };

    setup();
  }, [auth, dispatch, pendingSignup]);

  // check url params and switch to that location if necessary
  React.useEffect(() => {
    const { search } = window.location; // Get the query string
    const _business = new URLSearchParams(search).get('_location');

    // check if _business is a valid hex string
    const checkForHexRegExp = /^[0-9a-fA-F]{24}$/;

    if (!checkForHexRegExp.test(_business)) {
      // if not a valid hex string, return
      return;
    }

    // check what the _temporaryBusiness is in auth, if it is different from the current activeBusiness, switch to that location
    if (auth._temporaryBusiness === _business) {
      return;
    }

    // if the _temporaryBusiness is different from the activeBusiness, switch to that location
    switchUser({ _business, goToRootUrl: true });
  }, [auth._temporaryBusiness]);

  const handleLogout = async () => {
    // stop this browser's desktop notifications before the session ends
    await dispatch(handleDisableNotificationsOnLogout());
    store.remove('jwt');
    window.location = `https://${window.location.hostname}/api/logout`;
  };

  if (!showNewLayout) {
    return (
      <div className="border-b">
        <div className="w-full flex h-16 items-center justify-between px-2">
          <div className="flex flex-row justify-start">
            <AccountSwitcher
              businessGroup={businessGroup}
              isEnterpriseEnabled={isEnterpriseEnabled}
              handleSwitchLocation={handleSwitchLocation}
              auth={auth}
            />
            {!auth.activated && auth.reseller && (
              <p role="status" className="ml-2 self-center text-sm text-destructive">
                {RESELLER_DEACTIVATED_MESSAGE}
              </p>
            )}
            {!auth.activated && !auth.reseller && (
              <Dialog
                open={showReactivateAccountForm}
                onOpenChange={(x) => setShowReactivateAccountForm(x)}
              >
                <DialogTrigger asChild>
                  <Button
                    onClick={() => setShowReactivateAccountForm(true)}
                    className="ml-2"
                    variant="destructive"
                  >
                    Reactivate Account
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden">
                  <ReactivateAccountForm closeDialog={() => setShowReactivateAccountForm(false)} />
                </DialogContent>
              </Dialog>
            )}
            <TooltipProvider className="flex md:hidden">
              <Tooltip delayDuration={0}>
                <DropdownMenu className="flex md:hidden">
                  <TooltipTrigger>
                    <DropdownMenuTrigger className="outline-hidden">
                      <Button className="flex md:hidden ml-2" variant="ghost">
                        {(unreadConversations > 0 ||
                          unreadTeamConversationsLength > 0 ||
                          overdueCount > 0 ||
                          dueTodayCount > 0) && (
                          <Dot className="text-primary size-8 absolute mr-8" />
                        )}
                        <Menu className="size-5" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuGroup>
                      <NavigationSubMenu
                        title="Runner"
                        components={runnerComponents}
                        activeDashboard={activeDashboard}
                        unreadConversations={unreadConversations}
                        unreadTeamConversationsLength={unreadTeamConversationsLength}
                        overdueCount={overdueCount}
                        dueTodayCount={dueTodayCount}
                        setMenuTitle={setMenuTitle}
                      />
                      <NavigationSubMenu
                        title="Messenger"
                        components={filteredMessengerComponents}
                        activeDashboard={activeDashboard}
                        setCustomerConversationAssignedType={
                          handleSetCustomerConversationAssignedType
                        }
                        unreadConversations={unreadConversations}
                        unreadTeamConversationsLength={unreadTeamConversationsLength}
                        overdueCount={overdueCount}
                        dueTodayCount={dueTodayCount}
                        setMenuTitle={setMenuTitle}
                      />
                      <NavigationSubMenu
                        title="Reviews"
                        components={reviewComponents}
                        activeDashboard={activeDashboard}
                        overdueCount={overdueCount}
                        dueTodayCount={dueTodayCount}
                        setMenuTitle={setMenuTitle}
                      />
                      <NavigationSubMenu
                        title="Automation"
                        components={automationComponents}
                        activeDashboard={activeDashboard}
                        overdueCount={overdueCount}
                        dueTodayCount={dueTodayCount}
                        setMenuTitle={setMenuTitle}
                      />
                      <NavigationSubMenu
                        title="Chekkit Pay"
                        components={payComponents}
                        activeDashboard={activeDashboard}
                        overdueCount={overdueCount}
                        dueTodayCount={dueTodayCount}
                        setMenuTitle={setMenuTitle}
                      />
                      <NavigationSubMenu
                        title="Forms"
                        components={questionnaireComponents}
                        activeDashboard={activeDashboard}
                        overdueCount={overdueCount}
                        dueTodayCount={dueTodayCount}
                        setMenuTitle={setMenuTitle}
                      />
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <TooltipContent>
                  <p>{activeDashboard === 'settings' ? 'Settings' : menuTitle}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="hidden md:flex flex-row">
            {isEnterpriseEnabled ? (
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className={
                        filteredMessengerComponents.find((c) => c.name === activeDashboard)
                          ? 'text-primary'
                          : ''
                      }
                      onClick={() => {
                        handleSetCustomerConversationAssignedType('ALL');
                      }}
                    >
                      <div className="flex flex-row items-center justify-center">
                        {(unreadConversations > 0 ||
                          unreadTeamConversationsLength > 0 ||
                          overdueCount > 0 ||
                          dueTodayCount > 0) && (
                          <Dot
                            className="size-8"
                            style={{
                              marginRight: -8,
                            }}
                          />
                        )}
                        Messenger
                      </div>
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[300px] gap-3 p-4 md:w-[400px] md:grid-cols-2 lg:w-[500px] ">
                        {enterpriseMessengerComponents.map((component) => (
                          <ListItem
                            key={component.title}
                            component={component}
                            activeDashboard={activeDashboard}
                            setCustomerConversationAssignedType={
                              handleSetCustomerConversationAssignedType
                            }
                            unreadConversations={unreadConversations}
                            unreadTeamConversationsLength={unreadTeamConversationsLength}
                            overdueCount={overdueCount}
                            dueTodayCount={dueTodayCount}
                          />
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            ) : (
              <>
                <NavigationMenu>
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        className={
                          filteredMessengerComponents.find((c) => c.name === activeDashboard)
                            ? 'text-primary'
                            : ''
                        }
                        onClick={() => {
                          handleSetCustomerConversationAssignedType('ALL');
                        }}
                      >
                        <div className="flex flex-row items-center justify-center">
                          {(unreadConversations > 0 ||
                            unreadTeamConversationsLength > 0 ||
                            overdueCount > 0 ||
                            dueTodayCount > 0) && (
                            <Dot
                              className="size-8"
                              style={{
                                marginRight: -8,
                              }}
                            />
                          )}
                          Messenger
                        </div>
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[300px] gap-3 p-4 md:w-[400px] md:grid-cols-2 lg:w-[500px] ">
                          {filteredMessengerComponents.map((component) => (
                            <ListItem
                              key={component.title}
                              component={component}
                              activeDashboard={activeDashboard}
                              setCustomerConversationAssignedType={
                                handleSetCustomerConversationAssignedType
                              }
                              unreadConversations={unreadConversations}
                              unreadTeamConversationsLength={unreadTeamConversationsLength}
                              overdueCount={overdueCount}
                              dueTodayCount={dueTodayCount}
                            />
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>

                <NavigationMenu>
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        className={
                          reviewComponents.find((c) => c.name === activeDashboard)
                            ? 'text-primary'
                            : ''
                        }
                      >
                        Reviews
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid gap-3 p-6 md:w-[300px] lg:w-[400px] lg:grid-cols-[.75fr_1fr]">
                          <li className="row-span-3">
                            <NavigationMenuLink asChild>
                              <span className="flex h-full bg-muted w-full select-none flex-col justify-end rounded-md bg-linear-to-b from-muted/50 to-muted p-6 no-underline outline-hidden focus:shadow-md">
                                <div className="mb-2 mt-4 text-lg font-medium">
                                  Review Management
                                </div>
                                <p className="text-sm leading-tight text-muted-foreground">
                                  Get hundreds of reviews on any platform you would like. Track your
                                  teams progress and see how much you&apos;ve grown over time.
                                </p>
                              </span>
                            </NavigationMenuLink>
                          </li>
                          {reviewComponents.map((component) => (
                            <ListItem
                              key={component.title}
                              component={component}
                              activeDashboard={activeDashboard}
                            />
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
                <NavigationMenu>
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        className={
                          automationComponents.find((c) => c.name === activeDashboard)
                            ? 'text-primary'
                            : ''
                        }
                      >
                        Automation
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid gap-3 p-6 md:w-[300px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                          <li className="row-span-3">
                            <NavigationMenuLink asChild>
                              <span className="flex h-full bg-muted w-full select-none flex-col justify-end rounded-md bg-linear-to-b from-muted/50 to-muted p-6 no-underline outline-hidden focus:shadow-md">
                                <div className="mb-2 mt-4 text-lg font-medium">
                                  Message Automation
                                </div>
                                <p className="text-sm leading-tight text-muted-foreground">
                                  Automatically message your customers without touching a keyboard.
                                  Set up triggers to send messages based on events. We are here to
                                  help with integrations.
                                </p>
                              </span>
                            </NavigationMenuLink>
                          </li>

                          {automationComponents.map((component) => {
                            if (auth.reseller && component.name === 'zapier') {
                              return null;
                            }
                            return (
                              <ListItem
                                key={component.title}
                                component={component}
                                activeDashboard={activeDashboard}
                                setCustomerConversationAssignedType={
                                  handleSetCustomerConversationAssignedType
                                }
                              />
                            );
                          })}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
                {!auth.reseller && (
                  <NavigationMenu>
                    <NavigationMenuList>
                      <NavigationMenuItem>
                        <NavigationMenuTrigger
                          className={
                            payComponents.find((c) => c.name === activeDashboard)
                              ? 'text-primary'
                              : ''
                          }
                        >
                          Chekkit Pay
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="grid gap-3 p-6 md:w-[300px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                            <li className="row-span-3">
                              <NavigationMenuLink asChild>
                                <span className="flex h-full bg-muted w-full select-none flex-col justify-end rounded-md bg-linear-to-b from-muted/50 to-muted p-6 no-underline outline-hidden focus:shadow-md">
                                  <div className="mb-2 mt-4 text-lg font-medium">Pay</div>
                                  <p className="text-sm leading-tight text-muted-foreground">
                                    Shorten payment cycles, expand payment options, and get money in
                                    your pocket quickly.
                                  </p>
                                </span>
                              </NavigationMenuLink>
                            </li>
                            {payComponents.map((component) => (
                              <ListItem
                                key={component.title}
                                component={component}
                                activeDashboard={activeDashboard}
                              />
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                )}
                {auth.reseller && auth.products?.includes('payments') && (
                  <NavigationMenu>
                    <NavigationMenuList>
                      <NavigationMenuItem>
                        <NavigationMenuTrigger
                          className={
                            payComponents.find((c) => c.name === activeDashboard)
                              ? 'text-primary'
                              : ''
                          }
                        >
                          Chekkit Pay
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="grid gap-3 p-6 md:w-[300px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                            <li className="row-span-3">
                              <NavigationMenuLink asChild>
                                <span className="flex h-full bg-muted w-full select-none flex-col justify-end rounded-md bg-linear-to-b from-muted/50 to-muted p-6 no-underline outline-hidden focus:shadow-md">
                                  <div className="mb-2 mt-4 text-lg font-medium">Pay</div>
                                  <p className="text-sm leading-tight text-muted-foreground">
                                    Shorten payment cycles, expand payment options, and get money in
                                    your pocket quickly.
                                  </p>
                                </span>
                              </NavigationMenuLink>
                            </li>
                            {payComponents.map((component) => (
                              <ListItem
                                key={component.title}
                                component={component}
                                activeDashboard={activeDashboard}
                              />
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                )}
                <NavigationMenu className="z-10">
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        className={
                          questionnaireComponents.find((c) => c.name === activeDashboard)
                            ? 'text-primary'
                            : ''
                        }
                      >
                        Forms
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid gap-3 p-6 md:w-[300px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                          <li className="row-span-3">
                            <NavigationMenuLink asChild>
                              <span className="flex h-full bg-muted w-full select-none flex-col justify-end rounded-md bg-linear-to-b from-muted/50 to-muted p-6 no-underline outline-hidden focus:shadow-md">
                                <div className="mb-2 mt-4 text-lg font-medium">Forms</div>
                                <p className="text-sm leading-tight text-muted-foreground">
                                  Create forms to collect intake information, gather post-sale
                                  feedback, and learn more about your customers. Send over text or
                                  email.
                                </p>
                              </span>
                            </NavigationMenuLink>
                          </li>
                          {questionnaireComponents.map((component) => (
                            <ListItem
                              key={component.title}
                              component={component}
                              activeDashboard={activeDashboard}
                            />
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              </>
            )}
          </div>
          <div className="flex flex-row items-center">
            {!pendingSignup && <PhoneVerificationChecker />}
            {!pendingSignup && <ConnectionChecker />}

            {!auth.reseller && <AnnouncementButton />}
            <ModeToggle />
            <NavDropdownMenu handleLogout={handleLogout} />
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function NavigationSubMenu({
  title,
  components,
  activeDashboard,
  setCustomerConversationAssignedType,
  setMenuTitle,
  unreadConversations,
  unreadTeamConversationsLength,
  overdueCount,
  dueTodayCount,
}) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <div className="px-1">
          {title === 'Messenger' &&
            (unreadConversations > 0 ||
              unreadTeamConversationsLength > 0 ||
              overdueCount > 0 ||
              dueTodayCount > 0) && (
              <Dot className="text-primary size-8 absolute" style={{ left: -5, top: 5 }} />
            )}
          <span
            className={cn(components.find((c) => c.name === activeDashboard) ? 'text-primary' : '')}
          >
            {title}
          </span>
        </div>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {components.map((component) => (
          <DropdownMenuItem
            key={component.name}
            onSelect={() => {
              if (setCustomerConversationAssignedType) {
                setCustomerConversationAssignedType('ALL');
              }
              setMenuTitle(title);
            }}
            className={cn(
              activeDashboard === component.name || activeDashboard === '' ? 'text-primary' : '',
              'px-2 py-2',
            )}
          >
            {component.title === 'Inbox' && unreadConversations > 0 && (
              <Dot className="text-primary size-8 absolute" style={{ left: -12, top: 2 }} />
            )}
            {component.title === 'Group Chat' && unreadTeamConversationsLength > 0 && (
              <Dot className="text-primary size-8 absolute" style={{ left: -12, top: 2 }} />
            )}
            {component.title === 'Reminders' && (overdueCount > 0 || dueTodayCount > 0) && (
              <Dot className="text-primary size-8 absolute" style={{ left: -12, top: 2 }} />
            )}
            <p
              className={cn(
                (unreadConversations ||
                  unreadTeamConversationsLength > 0 ||
                  overdueCount > 0 ||
                  dueTodayCount > 0) &&
                  'ml-1',
              )}
            >
              {component.title}
            </p>
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

const ListItem = React.forwardRef(
  (
    { className, component, activeDashboard, setCustomerConversationAssignedType, ...props },
    ref,
  ) => {
    return (
      <li className="relative">
        {component.title === 'Inbox' && props.unreadConversations > 0 && (
          <Dot className="text-primary size-8 absolute" style={{ left: -12, top: 2 }} />
        )}
        {component.title === 'Group Chat' && props.unreadTeamConversationsLength > 0 && (
          <Dot className="text-primary size-8 absolute" style={{ left: -12, top: 2 }} />
        )}
        {component.title === 'Reminders' && (props.overdueCount > 0 || props.dueTodayCount > 0) && (
          <Dot className="text-primary size-8 absolute" style={{ left: -12, top: 2 }} />
        )}
        <NavigationMenuLink asChild>
          <Link
            ref={ref}
            to={`/${component.name}`}
            className={cn(
              'cursor-pointer block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
              className,
              activeDashboard === component.name && 'text-primary',
            )}
            onClick={() => {
              component.type && setCustomerConversationAssignedType(component.type);
            }}
            {...props}
          >
            <div className="flex flex-row items-center justify-start">
              <div className="text-sm font-medium leading-none">{component.title}</div>
            </div>
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
              {component.description}
            </p>
          </Link>
        </NavigationMenuLink>
      </li>
    );
  },
);

ListItem.displayName = 'ListItem';
